import { CalendarColumnCellContent } from "@tento-ui/components/calendar/calendar-column-cell-content";
import { CalendarColumnLayoutGroup } from "@tento-ui/components/calendar/calendar-column-layout-group";
import { CalendarIcon, LonaIcons } from "@tento-ui/ui/icons";
import {
  CalendarSubcolumn,
  type CalendarSubcolumnSpan,
} from "@tento-lona/sheets-ui/sheet/calendar-subcolumn.ts";
import { DateTime, Utc } from "@tento-chrono";
import { type Option, RowId, TaskId } from "@tento-lona/sheets";
import type { RowLocalId, Unsubscribe } from "@tento-lona/sheets";
import {
  type BindCellContext,
  type ContentProviders,
  type RenderPlugin,
} from "@tento-lona/sheets-ui/sheet/render-plugin.ts";
import type { SheetDelegate } from "@tento-lona/sheets-ui/sheet/sheet-delegate.ts";
import { SheetLegend } from "@tento-lona/sheets-ui/sheet/sheet-legend.ts";
import { type SheetRow } from "@tento-lona";
import { SheetViewModel } from "@tento-lona";
import type { SheetBinder } from "../../sheet/sheet-index-renderer.ts";

/**
 * An ephemeral-only row type that previews a candidate schedule: each cell
 * on the calendar is one `CandidateAssignment` of the selected
 * `CandidateProposal`.
 *
 * The plugin reads from the module-singleton `CandidatePreviewStore`
 * rather than from row attributes — there is no "set a single attribute
 * on an ephemeral row" SDK API, so attribute-change reconciliation
 * isn't viable. The host writes the proposals to the store; the plugin
 * subscribes and re-renders cells on change.
 */

/**
 * One task of a candidate schedule: which task (`taskId`), its display id
 * and title when the proposal carries them, the proposed start and end
 * (RFC 3339 timestamps), and the proposal's reason for the slot. A field
 * the proposal leaves out is `null`.
 */
export interface CandidateAssignment {
  taskId: TaskId;
  displayId: Option<string>;
  title: Option<string>;
  scheduledStart: string;
  scheduledEnd: string;
  rationale: Option<string>;
}

/** A proposed schedule: its label, rationale and task assignments. */
export interface CandidateProposal {
  id: string;
  label: string;
  rationale: string;
  assignments: CandidateAssignment[];
}

interface CandidateSlot {
  id: string;
  timeRange: DateTime.Range<Utc>;
  taskId: TaskId;
  displayId: Option<string>;
  title: Option<string>;
}

/**
 * Reactive holder for the in-flight candidate preview. The host writes;
 * `CandidateAssignmentsSourcePlugin` reads + subscribes.
 *
 * Module singleton because the plugin and the host code that writes the
 * preview are built on different paths through a host's bootstrap — a
 * singleton avoids dependency-order plumbing.
 */
class CandidatePreviewStoreImpl {
  #state: Option<{ proposals: CandidateProposal[]; selectedIndex: number }> =
    null;
  #listeners = new Set<() => void>();

  set(proposals: CandidateProposal[], selectedIndex: number = 0): void {
    this.#state = { proposals, selectedIndex };
    this.#notify();
  }

  setSelectedIndex(index: number): void {
    if (!this.#state) return;
    if (index < 0 || index >= this.#state.proposals.length) return;
    if (index === this.#state.selectedIndex) return;
    this.#state = { ...this.#state, selectedIndex: index };
    this.#notify();
  }

  clear(): void {
    if (!this.#state) return;
    this.#state = null;
    this.#notify();
  }

  current(): Option<{ proposals: CandidateProposal[]; selectedIndex: number }> {
    return this.#state;
  }

  subscribe(listener: () => void): Unsubscribe {
    this.#listeners.add(listener);
    return () => this.#listeners.delete(listener);
  }

  #notify(): void {
    for (const listener of this.#listeners) listener();
  }
}

/**
 * The proposals being previewed on the calendar, shared by the
 * candidate-assignments plugin.
 */
export const CandidatePreviewStore: CandidatePreviewStoreImpl =
  new CandidatePreviewStoreImpl();

/**
 * The source-row plugin that previews proposed task assignments on the
 * calendar.
 */
export class CandidateAssignmentsSourcePlugin
  implements RenderPlugin.SourceRow<HTMLElement, SheetLegend.DefaultRow>
{
  readonly role = "source";
  readonly id = "candidate-assignments";
  readonly icon: HTMLTemplateElement = LonaIcons.$template(CalendarIcon);
  makeRowOptions = null;
  loadData = null;
  legendFactory = null;

  #candidateRowIds = new Set<RowLocalId>();
  #boundRows = new Map<
    RowLocalId,
    { handle: SheetDelegate; row: SheetRow.Joined.Handle }
  >();
  #unsubscribe: Option<() => void> = null;

  isCandidateRow(rowId: RowLocalId): boolean {
    return this.#candidateRowIds.has(rowId);
  }

  optionsInfo(_row: SheetRow.Joined.Handle): Option<string> {
    return null;
  }

  makeCell(): HTMLElement {
    return document.createElement("div");
  }

  bindCell(_$cell: HTMLElement, _context: BindCellContext): void {}

  onBindRow(handle: SheetDelegate, row: SheetRow.Joined.Handle): void {
    if (!this.#candidateRowIds.has(row.id)) return;
    this.#boundRows.set(row.id, { handle, row });
    this.#ensureSubscribed();
  }
  onResize(): void {}
  onViewportColumnsChanged(): void {}
  onRenderedColumnsWillChange(): void {}

  #ensureSubscribed(): void {
    if (this.#unsubscribe) return;
    this.#unsubscribe = CandidatePreviewStore.subscribe(() => {
      for (const { handle, row } of this.#boundRows.values()) {
        handle.rebind.rebindRow(row);
      }
    });
  }

  #calendarProvider: CalendarSubcolumn.Provider = ((
    self,
  ) => ({
    subcolumnFromRow(
      _sheet: SheetViewModel.Surface<SheetBinder>,
      row: SheetRow.Joined.Handle,
      _parentRow: SheetRow.Joined.Handle,
    ): CalendarSubcolumn {
      const rowId = row.id;
      self.#candidateRowIds.add(rowId);

      // Per-row rebind handles are registered via `onBindRow` and the
      // store subscription fires `handle.rebind.rebindRow(row)` for each
      // when the active proposal flips.

      return {
        rowId: RowId.fromLocalId(rowId),
        width: { type: "natural" },
        loadData: async () => {},
        makeLayout: () => CalendarColumnLayoutGroup.make(),
        makeSpans: async ({ windowed }) => {
          const state = CandidatePreviewStore.current();
          if (!state) return [];
          const proposal = state.proposals[state.selectedIndex];
          if (!proposal) return [];
          const range = new DateTime.Range(
            windowed.fragment.start.toUtc(),
            windowed.fragment.end.toUtc(),
          );
          const spans: CalendarSubcolumnSpan[] = [];
          for (const a of proposal.assignments) {
            const startRes = DateTime.fromRfc3339(a.scheduledStart);
            const endRes = DateTime.fromRfc3339(a.scheduledEnd);
            const startDt = startRes.asOk();
            const endDt = endRes.asOk();
            if (startDt == null || endDt == null) continue;
            const start = startDt.toUtc();
            const end = endDt.toUtc();
            if (end.compare(range.start) <= 0) continue;
            if (start.compare(range.end) >= 0) continue;

            const id = `candidate-${proposal.id}-${a.taskId}`;
            const label = a.displayId ?? a.title ?? "";
            spans.push({
              id,
              start,
              end,
              pointerEvents: "none",
              item: null,
              callbacks: null,
              bindContent: (existing) => {
                const $content = existing instanceof CalendarColumnCellContent
                  ? existing
                  : CalendarColumnCellContent.make();
                // Through the chip's label part: text written on the chip
                // itself is not a part, and the chip drops it (with its
                // detached parts) on its next connect.
                $content.bindLabel(label);
                $content.style.fontSize = "var(--text-9px)";
                $content.style.padding = "2px 4px";
                return $content;
              },
              applyToCell: ($cell) => {
                $cell.style.setProperty(
                  "--std-calendar-cell-background",
                  "var(--accent)",
                );
                $cell.style.opacity = "0.35";
                $cell.style.borderRadius = "4px";
                $cell.style.border = "1px dashed var(--accent)";
              },
            });
          }
          return spans;
        },
      };
    },
  }))(this);

  contentProviders: ContentProviders = {
    calendar: this.#calendarProvider,
  };
}
